import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import {
  hasTimes,
  parseImageList,
  parseScheduleRows,
  parseTableRows,
} from "@shared/signage-rows";
import { MAX_SLIDESHOW_IMAGES } from "@shared/types/signage-template";
import { iframeSandbox } from "./iframeSandbox";
import { elfsightShareLink } from "@shared/elfsight";
import { claimWebFrame, type FrameHandle } from "./webFrameHost";
import type {
  ResolvedSignageTemplate,
  SignageRegionStyle,
  SignageTemplateRegion,
} from "@shared/types/signage-template";

/**
 * Draw one filled-in slide on a wall.
 *
 * The counterpart of `CustomScreen` for signage, and the third instance of the
 * pattern this repo already runs twice (`CustomScreen`, `OffersScreen`): ONE
 * renderer, driven entirely by data, with no per-template code anywhere.
 *
 * ## Everything is in CANVAS pixels, and the canvas is scaled once
 *
 * A region's rect, its font size and its corner radius are all expressed in the
 * template's own coordinate space (1920x1080 by default). The whole canvas is
 * then scaled by a single `transform`, so no region does arithmetic of its own
 * and a slide designed at 1920 looks identical on a 4K totem and in a 460px CMS
 * preview.
 *
 * ⚠️ **Deliberately not container queries.** `cqi`/`cqw` would express this
 * more neatly and DO NOT EXIST on the Tizen 7 floor (Chromium 94) - this repo
 * already deleted a `fontSizeCss()` that used them, for exactly that reason.
 * Measure the viewport, compute a scale, use `transform`.
 *
 * ## An empty value renders NOTHING
 *
 * Never a placeholder, never the field's name. A wall showing `{{price}}` to a
 * hotel guest is worse than a wall showing one fewer thing, and the filler's
 * own form is where a missing value should be pointed out.
 */

export interface TemplateSlideProps {
  slide: ResolvedSignageTemplate;
  /** IANA zone of the premise - what a `clock` region reads. */
  timezone: string;
  /** Injectable for tests; defaults to the real clock. */
  now?: () => Date;
  /**
   * WALL ONLY: web blocks borrow a long-lived frame from `webFrameHost`
   * instead of drawing their own, so the page is loaded once and survives the
   * loop (Elfsight meters every load). The CMS leaves this off and draws
   * inline. See `webFrameHost.ts`.
   */
  keepAliveWeb?: boolean;
}

/**
 * The size of the box this slide was given.
 *
 * ⚠️ **Measured from the element, not from `window`.** The first version read
 * `window.innerWidth/innerHeight`, reasoning that the signage surface is
 * `position: fixed; inset: 0` so the window IS the box. True on a wall, and
 * wrong everywhere else: put the slide in a 1200px dialog on a 1920px display
 * and it computed a scale of 1, drew the canvas at full size, and overflowed -
 * a 128px headline running off both edges. Reported from the CMS on the first
 * click-through.
 *
 * A `ResizeObserver` on the root is right in both cases, because on a wall the
 * root's box and the viewport are the same thing. It also picks up a dialog
 * being resized, which a window listener never would.
 */
function useBoxSize(ref: React.RefObject<HTMLDivElement | null>): {
  w: number;
  h: number;
} {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/** What a region actually draws: its field's value, or the designer's own. */
export function regionValue(
  region: SignageTemplateRegion,
  values: Record<string, string>
): string {
  return region.binding.kind === "field"
    ? (values[region.binding.field] ?? "")
    : region.binding.value;
}

/**
 * Flex alignment from the two style axes.
 *
 * Split out because `text` is the only kind that uses both, and reading
 * `alignItems` off `verticalAlign` inline is the sort of thing that gets
 * transposed once and looks like a rendering bug.
 */
function boxAlignment(style: SignageRegionStyle | undefined): {
  justifyContent: string;
  alignItems: string;
  textAlign: "left" | "center" | "right";
} {
  const align = style?.align ?? "left";
  const vertical = style?.verticalAlign ?? "top";
  return {
    justifyContent:
      align === "center"
        ? "center"
        : align === "right"
          ? "flex-end"
          : "flex-start",
    alignItems:
      vertical === "middle"
        ? "center"
        : vertical === "bottom"
          ? "flex-end"
          : "flex-start",
    textAlign: align,
  };
}

/** `HH:mm` in the premise's zone - never the panel's, which is often wrong. */
function clockIn(timezone: string, at: Date): string {
  try {
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(at);
  } catch {
    // An unknown zone is a configuration fault reported elsewhere (see the
    // resolver's `invalid_timezone` warning); a slide still has to draw.
    return new Intl.DateTimeFormat("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(at);
  }
}

function Region({
  region,
  values,
  timezone,
  at,
  scale,
  keepAliveWeb,
}: {
  region: SignageTemplateRegion;
  values: Record<string, string>;
  timezone: string;
  at: Date;
  scale: number;
  keepAliveWeb: boolean;
}) {
  const style = region.style;
  const value = regionValue(region, values);

  const frame: React.CSSProperties = {
    position: "absolute",
    left: region.rect.x,
    top: region.rect.y,
    width: region.rect.w,
    height: region.rect.h,
    overflow: "hidden",
    ...(style?.radius ? { borderRadius: style.radius } : {}),
    ...(style?.opacity !== undefined ? { opacity: style.opacity } : {}),
  };

  switch (region.kind) {
    case "shape":
      // The only kind with no content: a brand bar, a scrim, a rule. Its
      // `background` IS the region, so it needs no child.
      return (
        <div style={{ ...frame, background: style?.background ?? "#000000" }} />
      );

    case "text": {
      if (!value.trim()) return null;
      const { justifyContent, alignItems, textAlign } = boxAlignment(style);
      return (
        <div
          style={{
            ...frame,
            display: "flex",
            justifyContent,
            alignItems,
            ...(style?.background ? { background: style.background } : {}),
          }}
        >
          <div
            style={{
              color: style?.color ?? "#ffffff",
              fontSize: style?.fontSize ?? 48,
              fontWeight: style?.fontWeight ?? 600,
              ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
              lineHeight: style?.lineHeight ?? 1.2,
              ...(style?.letterSpacing
                ? { letterSpacing: style.letterSpacing }
                : {}),
              textAlign,
              // Authored line breaks survive; a designer who typed two lines
              // meant two lines.
              whiteSpace: "pre-wrap",
              width: "100%",
              ...(style?.uppercase ? { textTransform: "uppercase" } : {}),
            }}
          >
            {value}
          </div>
        </div>
      );
    }

    case "list": {
      if (region.table) {
        return <TableRegion region={region} value={value} frame={frame} />;
      }
      const rows = parseScheduleRows(value);
      if (rows.length === 0) return null;
      const withTimes = hasTimes(rows);

      /*
       * Sized to FIT the block, not to a chosen font size.
       *
       * A designer picks a box; the filler decides how many rows go in it, and
       * they are not in the room together. So the row height is the block
       * divided by the rows, and the type is a fraction of that - twelve
       * classes and three both fill the same space and both stay readable.
       *
       * Capped by the role's own size so four rows in a tall block do not
       * become absurd, and floored so a long list stays legible rather than
       * shrinking to nothing. `MAX_SCHEDULE_ROWS` is what stops the floor
       * being reached in practice.
       */
      const rowHeight = region.rect.h / rows.length;
      const size = Math.max(
        22,
        Math.min(style?.fontSize ?? 48, Math.round(rowHeight * 0.52))
      );

      return (
        <div
          style={{
            ...frame,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            ...(style?.background ? { background: style.background } : {}),
          }}
        >
          {rows.map((row, i) => (
            <div
              key={`${row.time}-${row.text}-${i}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: Math.round(size * 0.6),
                height: rowHeight,
                color: style?.color ?? "#ffffff",
                fontSize: size,
                fontWeight: style?.fontWeight ?? 500,
                ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
                lineHeight: 1.15,
                overflow: "hidden",
              }}
            >
              {withTimes && (
                <span
                  style={{
                    // A fixed gutter so every row lines up, even the one with
                    // no time. Ragged times are what make a timetable look
                    // like a mistake.
                    minWidth: `${size * 3.2}px`,
                    flexShrink: 0,
                    fontWeight: 700,
                    // Tabular figures, so 11:00 and 07:30 occupy the same
                    // width and the column does not shimmer.
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.time}
                </span>
              )}
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.text}
              </span>
            </div>
          ))}
        </div>
      );
    }

    case "image":
      if (!value.trim()) return null;
      return (
        <img
          src={value}
          alt=""
          style={{
            ...frame,
            objectFit: style?.fit === "contain" ? "contain" : "cover",
          }}
        />
      );

    case "qr": {
      if (!value.trim()) return null;
      // Square, and sized to the SHORTER edge: a QR stretched to a rectangle
      // is a QR that does not scan, which is the only thing it has to do.
      const size = Math.max(1, Math.min(region.rect.w, region.rect.h));
      return (
        <div
          style={{
            ...frame,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // A fixed white plate, never the designer's background: a code on
            // a dark or coloured field is unreadable to most phone cameras.
            background: "#ffffff",
            padding: Math.round(size * 0.06),
          }}
        >
          <QRCode
            value={value}
            size={size - Math.round(size * 0.12)}
            level="M"
            fgColor={style?.color ?? "#000000"}
            bgColor="#ffffff"
          />
        </div>
      );
    }

    case "clock": {
      const { justifyContent, alignItems, textAlign } = boxAlignment(style);
      return (
        <div
          style={{
            ...frame,
            display: "flex",
            justifyContent,
            alignItems,
            ...(style?.background ? { background: style.background } : {}),
          }}
        >
          <div
            style={{
              color: style?.color ?? "#ffffff",
              fontSize: style?.fontSize ?? 72,
              fontWeight: style?.fontWeight ?? 600,
              ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
              textAlign,
              width: "100%",
              // Digits that do not jump as the minute changes. A clock whose
              // layout shifts every sixty seconds reads as a fault.
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {clockIn(timezone, at)}
          </div>
        </div>
      );
    }

    case "web":
      return (
        <WebRegion
          region={region}
          url={value}
          frame={frame}
          scale={scale}
          keepAlive={keepAliveWeb}
        />
      );

    case "slideshow":
      return <SlideshowRegion region={region} value={value} frame={frame} />;

    default:
      /*
       * A kind this build does not know - an older panel meeting a template
       * designed on a newer CMS, which is the normal state of a fleet.
       * Nothing, rather than a guess: the same stance the item renderer takes
       * for an unknown item kind, after it spent a while painting broken-image
       * glyphs across hotel lobbies.
       */
      return null;
  }
}

/**
 * Several pictures, crossfading.
 *
 * Every picture is mounted at once and only the current one is opaque. That
 * costs memory for up to {@link MAX_SLIDESHOW_IMAGES} decoded stills, and buys
 * the thing a wall needs: the next picture is already loaded when its turn
 * comes, so a slow network shows the previous one a little longer instead of
 * a black box. An `opacity` transition, which the Tizen floor has always had.
 */
function SlideshowRegion({
  region,
  value,
  frame,
}: {
  region: SignageTemplateRegion;
  value: string;
  frame: React.CSSProperties;
}) {
  const images = parseImageList(value, MAX_SLIDESHOW_IMAGES);
  const seconds = region.intervalSeconds ?? 8;
  const [index, setIndex] = useState(0);
  const count = images.length;

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(
      () => setIndex((i) => (i + 1) % count),
      Math.max(2, seconds) * 1000
    );
    return () => clearInterval(t);
  }, [count, seconds]);

  if (count === 0) return null;
  const current = index % count;
  return (
    <div style={frame} data-testid={`slideshow-${region.id}`}>
      {images.map((src, i) => (
        <img
          key={`${i}-${src}`}
          src={src}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: region.style?.fit === "contain" ? "contain" : "cover",
            opacity: i === current ? 1 : 0,
            transition: "opacity 800ms ease",
          }}
        />
      ))}
    </div>
  );
}

/**
 * A list drawn as a table: every `|` a column.
 *
 * Sized like the row list - the block's height divided by the rows - so a
 * designer's box holds three rows or twelve without anybody choosing a font
 * size. Columns share the width, except that the LAST one takes what is left:
 * tables on a wall are nearly always "short things, then a description".
 */
function TableRegion({
  region,
  value,
  frame,
}: {
  region: SignageTemplateRegion;
  value: string;
  frame: React.CSSProperties;
}) {
  const rows = parseTableRows(value);
  if (rows.length === 0) return null;
  const style = region.style;
  const columns = Math.max(...rows.map((r) => r.length));
  const rowHeight = region.rect.h / rows.length;
  const size = Math.max(
    22,
    Math.min(style?.fontSize ?? 48, Math.round(rowHeight * 0.5))
  );
  return (
    <div
      style={{
        ...frame,
        display: "grid",
        gridTemplateColumns:
          columns > 1 ? `repeat(${columns - 1}, max-content) 1fr` : "1fr",
        gridAutoRows: rowHeight,
        columnGap: Math.round(size * 0.9),
        alignItems: "center",
        color: style?.color ?? "#ffffff",
        fontSize: size,
        fontWeight: style?.fontWeight ?? 500,
        ...(style?.fontFamily ? { fontFamily: style.fontFamily } : {}),
        ...(style?.background ? { background: style.background } : {}),
        lineHeight: 1.15,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {rows.flatMap((row, r) =>
        Array.from({ length: columns }, (_, c) => (
          <div
            key={`${r}-${c}`}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(r === 0 && region.tableHeader
                ? { fontWeight: 700, opacity: 0.75 }
                : {}),
            }}
          >
            {row[c] ?? ""}
          </div>
        ))
      )}
    </div>
  );
}

/** Only an absolute http(s) address is ever handed to a frame. */
export function webRegionSrc(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/** Zoom, clamped to what the designer offers. 1 = as a browser draws it. */
export function webZoom(style: SignageRegionStyle | undefined): number {
  const z = Number(style?.zoom);
  return Number.isFinite(z) && z > 0 ? Math.min(3, Math.max(0.25, z)) : 1;
}

/**
 * A web page in a box.
 *
 * ## Zoom is a frame drawn at 1/zoom size and scaled back up
 *
 * A page lays itself out for the viewport it is given. Drawing the frame at
 * `w / zoom` and scaling it by `zoom` means a zoom of 2 gives the page HALF the
 * viewport and doubles every pixel - the same thing browser zoom does - rather
 * than blurring a 1920px page into a 600px box. `transform` rather than the
 * CSS `zoom` property, which Chromium 94 (the Tizen floor) implements
 * differently from everything else.
 *
 * ## Reload is a new element, not `location.reload()`
 *
 * The page is cross-origin, so nothing here may reach into it. Changing the
 * `key` remounts the frame, which is a clean load every time.
 *
 * ## An address that is not http(s) draws nothing
 *
 * The server refuses those on save; this is the renderer not trusting that, for
 * the same reason every other kind renders nothing on bad data.
 */
function WebRegion({
  region,
  url,
  frame,
  scale,
  keepAlive,
}: {
  region: SignageTemplateRegion;
  url: string;
  frame: React.CSSProperties;
  scale: number;
  keepAlive: boolean;
}) {
  // An Instagram block may hold whatever Elfsight thing a filler pasted (the
  // install code, a bare id); the server normalises on save, and this covers
  // every preview before it does.
  const src = webRegionSrc(
    region.source === "instagram" ? (elfsightShareLink(url) ?? url) : url
  );
  if (!src) return null;
  return keepAlive ? (
    <KeptWebRegion region={region} src={src} frame={frame} scale={scale} />
  ) : (
    <InlineWebRegion region={region} src={src} frame={frame} />
  );
}

/**
 * The wall's web block: an empty placeholder with a long-lived frame from
 * `webFrameHost` laid over it. Claimed on mount, released (hidden, NOT
 * unloaded) on unmount, re-placed whenever the slide's scale or the block's
 * box changes.
 */
function KeptWebRegion({
  region,
  src,
  frame,
  scale,
}: {
  region: SignageTemplateRegion;
  src: string;
  frame: React.CSSProperties;
  scale: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const handle = useRef<FrameHandle | null>(null);
  const zoom = webZoom(region.style);
  const background = region.style?.background;
  const { x, y, w, h } = region.rect;

  useEffect(() => {
    const claimed = claimWebFrame(src, {
      refreshMinutes: region.refreshMinutes,
    });
    handle.current = claimed;
    return () => {
      claimed.release();
      handle.current = null;
    };
  }, [src, region.refreshMinutes]);

  // Declared AFTER the claim, so on a src change React runs the new claim
  // first and this places the frame it just got.
  useEffect(() => {
    const place = () => {
      const el = ref.current;
      const h2 = handle.current;
      if (!el || !h2) return;
      const box = el.getBoundingClientRect();
      h2.place({
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        viewportWidth: w / zoom,
        viewportHeight: h / zoom,
        background,
      });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [src, scale, zoom, x, y, w, h, background]);

  return (
    <div ref={ref} style={frame} data-testid={`web-region-${region.id}`} />
  );
}

/** The CMS's web block: an ordinary iframe, reloaded whenever it remounts. */
function InlineWebRegion({
  region,
  src,
  frame,
}: {
  region: SignageTemplateRegion;
  src: string;
  frame: React.CSSProperties;
}) {
  const [generation, setGeneration] = useState(0);
  const minutes = region.refreshMinutes ?? 0;
  useEffect(() => {
    if (!minutes || minutes <= 0) return;
    const t = setInterval(() => setGeneration((g) => g + 1), minutes * 60_000);
    return () => clearInterval(t);
  }, [minutes]);

  const zoom = webZoom(region.style);

  return (
    <div
      style={{
        ...frame,
        // What shows while the page loads, and behind a transparent one. The
        // designer's panel colour if there is one, else the page's usual white
        // would flash on a dark design.
        background: region.style?.background ?? "transparent",
      }}
    >
      <iframe
        key={generation}
        src={src}
        title=""
        // The page is decoration on a wall nobody can touch; it must not steal
        // focus from the surface or be reachable with a remote.
        tabIndex={-1}
        sandbox={iframeSandbox(src)}
        // No camera, microphone or location prompts on a lobby screen.
        allow=""
        // Default referrer, deliberately: feed-widget services (the usual
        // way to put Instagram on a wall) check the embedding domain against
        // the customer's allow-list, and `no-referrer` fails that check.
        data-testid={`web-region-${region.id}`}
        style={{
          border: 0,
          width: region.rect.w / zoom,
          height: region.rect.h / zoom,
          transform: `scale(${zoom})`,
          transformOrigin: "0 0",
          pointerEvents: "none",
          display: "block",
        }}
      />
    </div>
  );
}

export default function TemplateSlide({
  slide,
  timezone,
  now,
  keepAliveWeb = false,
}: TemplateSlideProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const box = useBoxSize(rootRef);
  const at = now ? now() : new Date();

  const { width, height } = slide.canvas;
  // `min`, so the whole design is visible: a slide is a composition and
  // cropping it would cut off whatever the designer put at the edge. The
  // letterboxing is the slide's own background, not black, so a design with a
  // colour fills the screen with it.
  //
  // Zero until the first measurement lands, which renders the canvas invisibly
  // small for one frame rather than at full size - the alternative is a flash
  // of a 1920px design inside a 400px box.
  const scale =
    box.w > 0 && box.h > 0
      ? Math.min(box.w / Math.max(1, width), box.h / Math.max(1, height))
      : 0;

  return (
    <div
      ref={rootRef}
      data-testid="signage-template-slide"
      style={{
        /*
         * `absolute`, not `fixed`. The PARENT decides where a slide lives: on a
         * wall `SignageSurface` already wraps this in a fixed, full-screen box,
         * and in the CMS it sits in a sized preview. A `fixed` root escaped
         * that box and had to be overridden with `!important` at both call
         * sites, which is the sort of fix that means the default is wrong.
         */
        position: "absolute",
        inset: 0,
        background: slide.background || "#000000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          width,
          height,
          transform: `scale(${scale})`,
          // The element keeps its unscaled size in layout, so the flex centring
          // above works on the SCALED result rather than the 1920px original.
          transformOrigin: "center center",
          background: slide.background || "transparent",
          flex: "0 0 auto",
        }}
      >
        {slide.regions.map((region) => (
          <Region
            key={region.id}
            region={region}
            values={slide.values}
            timezone={timezone}
            at={at}
            scale={scale}
            keepAliveWeb={keepAliveWeb}
          />
        ))}
      </div>
    </div>
  );
}
